import { CLM } from "../../../claims";
import { randomBeverageType_enum, randomBoolean, randomIntRange_, randomSapOrderNumber, randomSingleFrom, randomWholesaler_ } from "../../../claims-form-helpers";
import { CLP } from "../../../claims-process";
import { commandProvider, createCommandTestDescriptor } from "../../_common/command-descriptors";
import { testDefaults } from "../_common/test-defaults";
import { createClaimValidator } from "./LEGACY_CreateDraftClaim_V1.validator";
import { testDistributorLocations } from "@/common/test-data";

export type LEGACY_CreateDraftClaim_V1 = {
  schemaVersion:                  CLP.SchemaVersion;
  claimType:                      CLM.ClaimType;
  sapOrderNumber:                 string;
  beverageType:                   CLM.BeverageType;
  casesRequested:                 number;
  wholesaler_sapId?:              string;
  wholesaler_nameOverride?:       string;
  distributor?:                   CLM.DistributorReference;
  distributorLocation?:           CLM.DistributorLocationReference;
  claimNumberOverrides_prefix?:   number;
  claimNumberOverrides_sequence?: number;
};
export type RawLEGACY_CreateDraftClaim_V1 = Omit<LEGACY_CreateDraftClaim_V1, "claimType" | "beverageType" | "distributor" | "distributorLocation" | "schemaVersion"> & {
  schemaVersion:        string;
  claimType:            string;
  beverageType:         string;
  distributor?:         string;
  distributorLocation?: string;
};
export const toRawLEGACY_CreateDraftClaim_V1 = (_: LEGACY_CreateDraftClaim_V1): RawLEGACY_CreateDraftClaim_V1 => ({
  ..._,
  schemaVersion:        _.schemaVersion.identifier,
  claimType:            _.claimType   .identifier,
  beverageType:         _.beverageType.identifier,
  distributor:          _.distributor?.id.asStr,
  distributorLocation:  _.distributorLocation?.id.asStr,
});
export const fromRawLEGACY_CreateDraftClaim_V1 = (_: RawLEGACY_CreateDraftClaim_V1): LEGACY_CreateDraftClaim_V1 => ({
  ..._,
  claimType:     CLM.claimType   (_.claimType   ),
  beverageType:  CLM.beverageType(_.beverageType),
  distributor:         _.distributor         ? CLM.distributorReference        (_.distributor        ) : undefined,
  distributorLocation: _.distributorLocation ? CLM.distributorLocationReference(_.distributorLocation) : undefined,
  schemaVersion: CLP.schemaVersion(_.schemaVersion),
});

const generateCommand_random = (_: Partial<LEGACY_CreateDraftClaim_V1> = { }) => () => {
  const wholesaler              = randomWholesaler_();
  const pilotParticipant        = _.distributorLocation ? true : _.wholesaler_sapId ? false : randomBoolean();
  const testDistributorLocation = testDistributorLocations.random();

  return {
    schemaVersion:                 _.schemaVersion                 ?? CLP.SchemaVersion.V1,
    claimType:                     _.claimType                     ?? CLM.ClaimType.DESTRUCTION,
    sapOrderNumber:                _.sapOrderNumber                ?? randomSapOrderNumber(),
    beverageType:                  _.beverageType                  ?? randomBeverageType_enum(),
    casesRequested:                _.casesRequested                ?? randomIntRange_(1, 999),
    distributorLocation:            pilotParticipant ? _.distributorLocation     ?? CLM.distributorLocationReference(testDistributorLocation.id.asInt.toString(), testDistributorLocation.sapId) : undefined,
    wholesaler_sapId:              !pilotParticipant ? _.wholesaler_sapId        ?? wholesaler.sapId : undefined,
    wholesaler_nameOverride:       !pilotParticipant ? _.wholesaler_nameOverride ?? wholesaler.name  : undefined,
    claimNumberOverrides_prefix:   _.claimNumberOverrides_prefix   ?? undefined,
    claimNumberOverrides_sequence: _.claimNumberOverrides_sequence ?? undefined,
  };
};
const generateCommand_ttb          = () => generateCommand_random({ beverageType: CLM.BeverageType.BEER_FMB                                            });
const generateCommand_nonTtb       = () => generateCommand_random({ beverageType: randomSingleFrom([CLM.BeverageType.CIDER, CLM.BeverageType.SPIRITS]) });
const generateCommand_cider        = () => generateCommand_random({ beverageType: CLM.BeverageType.CIDER                                               });
const generateCommand_spirits      = () => generateCommand_random({ beverageType: CLM.BeverageType.SPIRITS                                             });
const generateCommand_nonAlcoholic = () => generateCommand_random({ beverageType: CLM.BeverageType.NON_ALCOHOLIC                                       });

export const LEGACY_CreateDraftClaim_V1 = (() => {
  const descriptor =  ({
    commandName:    "LEGACY_CreateDraftClaim_V1",
    commandToRaw:   toRawLEGACY_CreateDraftClaim_V1,
    commandFromRaw: fromRawLEGACY_CreateDraftClaim_V1,
  });
  const generated = {
    with:         (_: Partial<LEGACY_CreateDraftClaim_V1> = { }) => commandProvider(descriptor)({ command: generateCommand_random(_) }),
    random:       commandProvider(descriptor)({ command: generateCommand_random()       }),
    ttb:          commandProvider(descriptor)({ command: generateCommand_ttb()          }),
    nonTtb:       commandProvider(descriptor)({ command: generateCommand_nonTtb()       }),
    cider:        commandProvider(descriptor)({ command: generateCommand_cider()        }),
    spirits:      commandProvider(descriptor)({ command: generateCommand_spirits()      }),
    nonAlcoholic: commandProvider(descriptor)({ command: generateCommand_nonAlcoholic() }),
  };
  const tests = {
    createDraftClaim_random:           createCommandTestDescriptor({ commandUnderTestProvider: generated.random,                                                                           validator: createClaimValidator }),
    createDraftClaim_ttb:              createCommandTestDescriptor({ commandUnderTestProvider: generated.ttb,                                                                              validator: createClaimValidator }),
    createDraftClaim_nonTtb:           createCommandTestDescriptor({ commandUnderTestProvider: generated.nonTtb,                                                                           validator: createClaimValidator }),
    createDraftClaim_cider:            createCommandTestDescriptor({ commandUnderTestProvider: generated.cider,                                                                            validator: createClaimValidator }),
    createDraftClaim_spirits:          createCommandTestDescriptor({ commandUnderTestProvider: generated.spirits,                                                                          validator: createClaimValidator }),
    createDraftClaim_nonAlcoholic:     createCommandTestDescriptor({ commandUnderTestProvider: generated.nonAlcoholic,                                                                     validator: createClaimValidator }),
    createDraftClaim_overrideSequence: createCommandTestDescriptor({ commandUnderTestProvider: generated.with({ claimNumberOverrides_prefix: 12, claimNumberOverrides_sequence: 654321 }), validator: createClaimValidator }),
    createDraftClaim_overridePrefix:   createCommandTestDescriptor({ commandUnderTestProvider: generated.with({ claimNumberOverrides_prefix: 12                                        }), validator: createClaimValidator })
  };

  return { descriptor, generated, testDefaults, tests };
})();
